import React, { useState, useEffect, useMemo,useCallback  } from 'react';
import { getCustomerDetailAgentId, submitFinancialPlan, getFinancialGoals } from '../services/api.js';
import BuyingHouse from '../fpsections/BuyingHouse.js';
import BuyingCar from '../fpsections/BuyingCar.js';
import ChildEducation from '../fpsections/ChildEducation.js';
import ChildWedding from '../fpsections/ChildWedding.js';
import WorldTour from '../fpsections/WorldTour.js';
import StartingBusiness from '../fpsections/StartingBusiness.js';
import RetirementCorpus from '../fpsections/RetirementCorpus.js';

import '../css/FinancialPlanning.css';
import Modal from '../components/Modal';

// Enhanced safeClone function
const safeClone = (obj, seen = new WeakSet()) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (Array.isArray(obj)) return obj.map(item => safeClone(item, seen));
  if (obj instanceof HTMLElement || obj?.$$typeof) return null;
  
  if (seen.has(obj)) return '[Circular Reference]';
  seen.add(obj);

  const result = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (key.startsWith('__react') || key.startsWith('_react') || 
          key.includes('Fiber') || key.includes('Instance')) continue;
      result[key] = safeClone(obj[key], seen);
    }
  }
  return result;
};

const capitalizeName = (name) => {
  return name ? name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') : '';
};

const calculateAge = (dob, currentAge) => {
  if (currentAge !== undefined && currentAge !== null) return currentAge;
  if (!dob) return null;
  
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

// Consolidated goal configuration
const GOAL_CONFIG = {
  // Map frontend names to backend properties
  PROPERTY_MAP: {
    "Buying a home": "buyingHome",
    "Funding children's education": "childEducation",
    "Funding children's wedding": "childWedding",
    "Buying a Car": "buyingCar",
    "World Tour": "worldTour",
    "Starting a business": "startingBusiness",
    "Retirement corpus": "retirementCorpus"
  },
  
  // Options for each goal
  OPTIONS: {
    "Buying a home": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹25,00,000": "Less than ₹25,00,000",
      "₹25,00,000 - ₹50,00,000": "₹25,00,000 - ₹50,00,000",
      "₹50,00,000 - ₹1,00,00,000": "₹50,00,000 - ₹1,00,00,000",
      "₹1,00,00,000 - ₹2,00,00,000": "₹1,00,00,000 - ₹2,00,00,000",
      "More than ₹2,00,00,000": "More than ₹2,00,00,000",
    },
    "Funding children's education": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹10,00,000": "Less than ₹10,00,000",
      "₹10,00,000 - ₹25,00,000": "₹10,00,000 - ₹25,00,000",
      "₹25,00,000 - ₹50,00,000": "₹25,00,000 - ₹50,00,000",
      "₹50,00,000 - ₹1,00,00,000": "₹50,00,000 - ₹1,00,00,000",
      "More than ₹1,00,00,000": "More than ₹1,00,00,000",
    },
    "Funding children's wedding": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹10,00,000": "Less than ₹10,00,000",
      "₹10,00,000 - ₹25,00,000": "₹10,00,000 - ₹25,00,000",
      "₹25,00,000 - ₹50,00,000": "₹25,00,000 - ₹50,00,000",
      "₹50,00,000 - ₹1,00,00,000": "₹50,00,000 - ₹1,00,00,000",
      "More than ₹1,00,00,000": "More than ₹1,00,00,000",
    },
    "Buying a Car": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹3,00,000": "Less than ₹3,00,000",
      "₹3,00,000 - ₹5,00,000": "₹3,00,000 - ₹5,00,000",
      "₹5,00,000 - ₹10,00,000": "₹5,00,000 - ₹10,00,000", // Fixed formatting
      "₹10,00,000 - ₹15,00,000": "₹10,00,000 - ₹15,00,000",
      "More than ₹15,00,000": "More than ₹15,00,000",
    },
    "World Tour": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹10,00,000": "Less than ₹10,00,000",
      "₹10,00,000 - ₹25,00,000": "₹10,00,000 - ₹25,00,000",
      "₹25,00,000 - ₹50,00,000": "₹25,00,000 - ₹50,00,000",
      "₹50,00,000 - ₹1,00,00,000": "₹50,00,000 - ₹1,00,00,000",
      "More than ₹1,00,00,000": "More than ₹1,00,00,000",
    },
    "Starting a business": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "Less than ₹10,00,000": "Less than ₹10,00,000",
      "₹10,00,000 - ₹20,00,000": "₹10,00,000 - ₹20,00,000",
      "₹20,00,000 - ₹30,00,000": "₹20,00,000 - ₹30,00,000",
      "₹30,00,000 - ₹50,00,000": "₹30,00,000 - ₹50,00,000",
      "More than ₹50,00,000": "More than ₹50,00,000",
    },
    "Retirement corpus": {
      "Select an Option": "Select an Option",
      "Not Applicable": "Not Applicable",
      "₹50,00,000 - ₹1,00,00,000": "₹50,00,000 - ₹1,00,00,000",
      "₹1,00,00,000 - ₹5,00,00,000": "₹1,00,00,000 - ₹5,00,00,000",
      "₹5,00,00,000 - ₹10,00,00,000": "₹5,00,00,000 - ₹10,00,00,000",
      "More than ₹10,00,00,000": "More than ₹10,00,00,000",
    }
  },
  
  // Get all goal names
  ALL: () => Object.keys(GOAL_CONFIG.PROPERTY_MAP),
  
  // Get options for a specific goal
  getOptions: (goalName) => GOAL_CONFIG.OPTIONS[goalName] || {},
  
  // Get backend property name
  getProperty: (goalName) => GOAL_CONFIG.PROPERTY_MAP[goalName] || goalName
};
// Move InsuranceDetails component outside of FinancialPlanning
const InsuranceDetails = ({ formData, onFieldChange, onBack, onContinue }) => {
  return (
    <div className="insurance-details">
      <h3>Insurance Details</h3>
      <button className="back-button" onClick={onBack}>
        ← Back
      </button>
      
      <div className="form-group">
        <label>Health Insurance:</label>
        <input 
          type="text" 
          value={formData.health_insurance}
          onChange={(e) => onFieldChange('health_insurance', e.target.value)}            
          placeholder="Enter health insurance details"
        />
      </div>
      
      <div className="form-group">
        <label>Term Insurance:</label>
        <input 
          type="text" 
          value={formData.term_insurance}
          onChange={(e) => onFieldChange('term_insurance', e.target.value)}
          placeholder="Enter term insurance details"
        />
      </div>
      
      <div className="form-group">
        <label>Disability Insurance:</label>
        <input 
          type="text" 
          value={formData.disability_insurance}
          onChange={(e) => onFieldChange('disability_insurance', e.target.value)}
          placeholder="Enter disability insurance details"
        />
      </div>
      
      <div className="navigation-buttons">
        <button className="submit-button" onClick={onContinue}>
          Continue to Review
        </button>
      </div>
    </div>
  );
};

function FinancialPlanning() {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [showGoalSelection, setShowGoalSelection] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [goalsError, setGoalsError] = useState('');
  const [goalValues, setGoalValues] = useState({});
  const [newGoalDropdownValues, setNewGoalDropdownValues] = useState({});
  const [currentGoalIndex, setCurrentGoalIndex] = useState(0);
  const [goalPlanningData, setGoalPlanningData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [availableGoalsSelection, setAvailableGoalsSelection] = useState({});

  const [formData, setFormData] = useState({
    customerId: '',
    agentId: '',
    buyingHome: null,
    childEducation: null,
    childWedding: null,
    buyingCar: null,
    worldTour: null,
    startingBusiness: null,
    retirementCorpus: null,
    // Add new insurance fields
    health_insurance: '',
    term_insurance: '',
    disability_insurance: ''
  });

  // Get all possible goals
  const goalOptions = useMemo(() => GOAL_CONFIG.ALL(), []);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [customers, searchQuery]); 

  // Calculate parent's current age properly
  const parentCurrentAge = useMemo(() => {
    if (!customerData) return 0;
    return calculateAge(customerData.dob, customerData.customer_age) || 0;
  }, [customerData]);
  // Add this handler in the main component
// Remove the useCallback wrapper
const handleInsuranceChange = (field, value) => {
  setFormData(prev => ({ ...prev, [field]: value }));
};
  // Initialize available goals selection
  useEffect(() => {
    if (customerData) {
      const initialSelection = {};
      goalOptions.forEach(goal => {
        if (!selectedGoals.includes(goal)) {
          initialSelection[goal] = false;
        }
      });
      setAvailableGoalsSelection(initialSelection);
    }
  }, [customerData, selectedGoals, goalOptions]);

  // Toggle goal selection in Available Goals section
  const toggleAvailableGoal = (goal) => {
    setAvailableGoalsSelection(prev => ({
      ...prev,
      [goal]: !prev[goal]
    }));
  };

  // Add selected goals from Available section to Selected Goals section
  const addSelectedGoals = () => {
    const newlySelected = Object.entries(availableGoalsSelection)
      .filter(([goal, isSelected]) => isSelected)
      .map(([goal]) => goal);
    
    setSelectedGoals(prev => [...prev, ...newlySelected]);
    
    // Reset the checkboxes for added goals
    const updatedSelection = { ...availableGoalsSelection };
    newlySelected.forEach(goal => {
      updatedSelection[goal] = false;
    });
    setAvailableGoalsSelection(updatedSelection);
  };

  // Remove goal from Selected Goals section
  const removeSelectedGoal = (goal) => {
    setSelectedGoals(prev => prev.filter(g => g !== goal));
    
    // Update available goals selection
    setAvailableGoalsSelection(prev => ({
      ...prev,
      [goal]: false
    }));
    
    // Reset form data for this goal
    const backendProp = GOAL_CONFIG.getProperty(goal);
    setFormData(prev => ({ ...prev, [backendProp]: null }));
    
    // Reset dropdown value
    setNewGoalDropdownValues(prev => ({
      ...prev,
      [goal]: "Select an Option"
    }));
  };
// Add this useEffect to debug formData changes
useEffect(() => {
  console.log("Form data updated to check insurance :", formData);
}, [formData]);
  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const agentId = localStorage.getItem('agentId');
        if (!agentId) {
          setError("Agent ID not found. Please log in again.");
          setLoading(false);
          return;
        }
    
       const response = await getCustomerDetailAgentId(agentId, 'Financial Horoscope');
        
        // DEBUG: Log the raw API response
        console.log('Customers API response:', response);
        
        // Check if data exists and log customer details
        if (response.data && response.data.length > 0) {
          console.log('First customer in response:', response.data[0]);
          console.log('Customer fields:', Object.keys(response.data[0]));
        }
        
        setCustomers(response.data || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setError("Failed to fetch customers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch goals when customer is selected
  useEffect(() => {
    const fetchGoals = async () => {
      console.log('Starting fetchGoals');
      if (step === 2 && selectedCustomerId) {
        setLoadingGoals(true);
        setGoalsError('');

        try {
          console.log('Fetching goals for customer:', selectedCustomerId);
          const response = await getFinancialGoals(selectedCustomerId);
          const selectedCustomer = customers.find(
            cust => cust.id.toString() === selectedCustomerId
          );
          console.log('Received goals response:', response);
    
          // Merge customer data with goals
          setCustomerData({
            ...safeClone(selectedCustomer),
            ...safeClone(response.data)
          });
          // Initialize goal planning data with existing plans
          const initialGoalPlanningData = {};
          const fetchedPlanData = response.data.planData || {};
          console.log('fetchedPlanData', fetchedPlanData);

          response.data.existingGoals.forEach(goal => {
          initialGoalPlanningData[goal] = fetchedPlanData[goal] || {};
          });

          setGoalPlanningData(initialGoalPlanningData);
          // Process existing goals
          const fetchedExistingGoals = response.data.existingGoals || [];
          const fetchedGoalValues = response.data.goalValues || {};
          console.log('Fetched goal values:', fetchedGoalValues);
         
         

         setSelectedGoals(fetchedExistingGoals);

          // Initialize goal values
          const initialGoalValues = {};
          const initialDropdownValues = {};

          // Initialize all possible goals
          goalOptions.forEach(goal => {
            const backendProp = GOAL_CONFIG.getProperty(goal);
            
            // Try to find the goal value using multiple possible keys
            const possibleKeys = [
              backendProp, 
              goal,
              goal.toLowerCase().replace(/\s+/g, ''),
              goal.replace(/'/g, '').replace(/\s+/g, '')
            ];
            
            let goalValue = null;
            
            // Find the first matching key in the fetched values
            for (const key of possibleKeys) {
              if (fetchedGoalValues[key]) {
                goalValue = fetchedGoalValues[key];
                break;
              }
            }
            
            // Clean up currency symbols if needed
            if (goalValue && typeof goalValue === 'string') {
              goalValue = goalValue.replace(/\?/g, '₹');
            }

            if (fetchedExistingGoals.includes(goal)) {
              // Use the found value or default to "Select an Option"
              initialGoalValues[backendProp] = goalValue || null;
              initialDropdownValues[goal] = goalValue || "Select an Option";
            } else {
              // Initialize with default
              initialDropdownValues[goal] = "Select an Option";
            }
          });

          setGoalValues(initialGoalValues);
          setNewGoalDropdownValues(initialDropdownValues);
          console.log('Initial dropdown values:', initialDropdownValues);
          
          // Update form data
          setFormData(prev => ({
            ...prev,
            ...initialGoalValues,
            customerId: selectedCustomerId,
            agentId: localStorage.getItem('agentId') || ''
          }));

        } catch (error) {
          console.error("Error fetching financial goals:", error);
          setGoalsError("Failed to load customer goals. Please try again.");
        } finally {
          setLoadingGoals(false);
        }
      }
    };

    fetchGoals();
  }, [step, selectedCustomerId, customers, goalOptions]);

  // Handler for completing a goal planning step
  const handleGoalPlanningComplete = (goalData) => {
    const goalName = selectedGoals[currentGoalIndex];
    const backendProp = GOAL_CONFIG.getProperty(goalName);

    // Include the selected value from the dropdown
    const completeData = {
      ...goalData,
      selectedValue: newGoalDropdownValues[goalName]
    };
    
    setGoalPlanningData(prev => ({
      ...prev,
      [goalName]: goalData
    }));

    setFormData(prev => ({
      ...prev,
      [backendProp]: completeData
    }));
    
      // Move to next goal or insurance step if last goal
  if (currentGoalIndex < selectedGoals.length - 1) {
    setCurrentGoalIndex(prev => prev + 1);
  } else {
    setStep(4); // New insurance step
  }
  };


  // Handler for going back from goal planning
  const handleBackToGoals = () => {
    if (currentGoalIndex > 0) {
      setCurrentGoalIndex(prev => prev - 1);
    } else {
      setStep(2); // Back to goal selection
    }
  };

  // Handle form submission
  const handleFinalSubmit = async () => {
    try {
      // Prepare form data
      console.log('Form data being submitted:', JSON.stringify(formData, null, 2));
      
      const submitData = {
        ...formData,
        ...goalPlanningData,
        customerId: selectedCustomerId,
        agentId: localStorage.getItem('agentId') || ''
      };

      // Sanitize and submit
      const sanitizedData = safeClone(submitData);
      console.log('Submitting planData:', sanitizedData);
      
      const response = await submitFinancialPlan(sanitizedData);
      if (response.data) {
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error submitting financial plan:', error);
      alert(error.response?.data?.message || 'Failed to submit plan. Please try again.');
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setStep(1);
    setSelectedCustomerId('');
    setCustomerData(null);
    setSelectedGoals([]);
    setNewGoalDropdownValues({});
    setAvailableGoalsSelection({});
    setFormData({
      customerId: '', agentId: '', buyingHome: null, childEducation: null,
      childWedding: null, buyingCar: null, worldTour: null,
      startingBusiness: null, retirementCorpus: null,
      health_insurance: '',  term_insurance: '',  disability_insurance: ''
    });
  };

  // Handle dropdown change
  const handleDropdownChange = (goal, value) => {
    const backendProp = GOAL_CONFIG.getProperty(goal);
    
    // Update UI state
    setNewGoalDropdownValues(prev => ({ ...prev, [goal]: value }));
    
    // Update form data
    setFormData(prev => ({ ...prev, [backendProp]: value }));
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit'
      }).replace(/ /g, '-');
    } catch (e) {
      return 'N/A';
    }
  };

  // Calculate years to retirement
  const getYearsToRetirement = () => {
    if (!customerData?.dob || !customerData?.retirement_age) return 'N/A';
    
    const age = calculateAge(customerData.dob);
    if (age === null) return 'N/A';
    
    const yearsLeft = customerData.retirement_age - age;
    return yearsLeft > 0 ? yearsLeft : 'Retired';
  };

  // Render goal-specific planning component
  const renderGoalPlanning = () => {
    const currentGoalName = selectedGoals[currentGoalIndex];
    const existingData = goalPlanningData[currentGoalName] || {};

    console.log('renderGoalPlanning:', currentGoalName);
    console.log('renderGoalPlanning:', existingData);


    switch (currentGoalName) {
      case "Buying a home":
        return (
          <BuyingHouse
            goalName={currentGoalName}
            existingData={existingData}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
      case "Funding children's education":
        return (
          <ChildEducation
            goalName={currentGoalName}
            existingData={existingData}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
      case "Funding children's wedding":
        return (
          <ChildWedding
            goalName={currentGoalName}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
            parentCurrentAge={parentCurrentAge}
            parentRetirementAge={customerData.retirement_age ?? 0}
          />
        );
      case "Buying a Car":
        return (
          <BuyingCar
            goalName={currentGoalName}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
      case "World Tour":
        return (
          <WorldTour
            goalName={currentGoalName}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
      case "Starting a business":
        return (
          <StartingBusiness
            goalName={currentGoalName}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
      case "Retirement corpus":
        return (
          <RetirementCorpus
            goalName={currentGoalName}
            onGoalComplete={handleGoalPlanningComplete}
            onBackToGoals={handleBackToGoals}
          />
        );
        return (
          <div className="goal-planning-section">
            <button className="back-button" onClick={handleBackToGoals}>
              ← Back to Goals
            </button>
            <h4>Planning for: {currentGoalName}</h4>
            <p>You have selected the value for "{currentGoalName}" in the previous step.</p>
            <div className="action-buttons">
              <button
                className="satisfied-button"
                onClick={() => handleGoalPlanningComplete({
                  selectedValue: newGoalDropdownValues[currentGoalName]
                })}
              >
                Mark as Complete & Next
              </button>
            </div>
          </div>
        );
      default:
        return <p>Unknown goal type</p>;
    }
  };

  // Review and Submit Component
  const ReviewAndSubmit = () => (
    <div className="review-section">
      <button className="back-button" onClick={() => setStep(4)}>
        ← Back to Insurance
      </button>
      <h3>Review Your Financial Plan</h3>
      <div className="plan-summary">
        <h4>Plan Summary for {customerData?.name}</h4>

        {goalOptions.map((goal) => {
          const isSelected = selectedGoals.includes(goal);
          const displayValue = isSelected 
            ? (newGoalDropdownValues[goal] || 'Not selected') 
            : 'Not selected';

          return (
            <div key={goal} className="goal-summary">
              <h5>{goal}</h5>
              <p className={isSelected ? "completed" : "not-selected"}>
                {isSelected ? `✓ Selected: ${displayValue}` : 'Not selected'}
              </p>
            </div>
          );
        })}
      </div>
            {/* Add insurance summary */}
            
    <div className="insurance-summary">
      <h4>Insurance Details</h4>
      <p><strong>Health Insurance:</strong> {formData.health_insurance || 'Not provided'}</p>
      <p><strong>Term Insurance:</strong> {formData.term_insurance || 'Not provided'}</p>
      <p><strong>Disability Insurance:</strong> {formData.disability_insurance || 'Not provided'}</p>
    </div>
      <div className="final-submit">
        <button className="submit-button" onClick={handleFinalSubmit}>
          Submit Financial Plan
        </button>
      </div>
    </div>
  );

  return (
    <div className="financial-planning-container">
      <div className="financial-planning-form">
        <h1>Financial Planning</h1>
        
        {/* Step 1: Customer Selection */}
        {step === 1 && (
          <>
            {loading ? (
              <p className="info-text">Loading customers...</p>
            ) : error ? (
              <p className="error-message">{error}</p>
            ) : customers.length === 0 ? (
              <p className="no-customers">No customers found for financial planning.</p>
            ) : (
              <div className="customer-selection">
                <label htmlFor="customer-select-dropdown">Select Customer:</label>
                
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                
                <select
                  id="customer-select-dropdown"
                  value={selectedCustomerId}
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                  className="customer-select"
                >
                  <option value="">Select a customer</option>
                  {filteredCustomers.map(cust => (
                    <option key={cust.id} value={cust.id.toString()}>
                      {cust.name}
                    </option>
                  ))}
                </select>
                
                {selectedCustomerId && (
                  <div className="button-container">
                    <button className="continue-button" onClick={() => setStep(2)}>
                      Continue
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Step 2: Goal Selection */}
        {step === 2 && (
          <div className="goal-selection-container">
            <button className="back-button" onClick={() => setStep(1)}>
              ← Back to Customers
            </button>
            
            {loadingGoals ? (
              <div className="loading-container">
                <p className="info-text">Loading customer goals...</p>
              </div>
            ) : goalsError ? (
              <p className="error">{goalsError}</p>
            ) : customerData ? (
              <>
                <div className="customer-info">
                  <h4>Financial Planning for: {capitalizeName(customerData.name)}</h4>
                  
                  <div className="client-info-table">
                    <table>
                      <tbody>
                        <tr>
                          <td><strong>Client Name</strong></td>
                          <td>{customerData.name}</td>
                        </tr>
                        <tr>
                          <td><strong>Date of Birth</strong></td>
                          <td>{formatDate(customerData.dob)}</td>
                        </tr>
                        <tr>
                          <td><strong>Current Age</strong></td>
                          <td>{calculateAge(customerData.dob, customerData.customer_age) || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Retirement Age</strong></td>
                          <td>{customerData.retirement_age || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td><strong>Years to Retirement</strong></td>
                          <td>{getYearsToRetirement()}</td>
                        </tr>
                        <tr>
                          <td><strong>Date of Plan Creation</strong></td>
                          <td>{formatDate(new Date().toISOString())}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="section">
                  <h4>Selected Financial Goals</h4>
                  {selectedGoals.length > 0 ? (
                    <ul className="goal-list">
                      {selectedGoals.map((goal, index) => (
                        <li key={index}>
                          <div className="goal-name">
                            {goal}
                            <button 
                              className="remove-goal-button"
                              onClick={() => removeSelectedGoal(goal)}
                            >
                              ×
                            </button>
                          </div>
                          <div className="goal-value">
                            <select
                              className="goal-value-dropdown"
                              value={newGoalDropdownValues[goal] || "Select an Option"}
                              onChange={(e) => handleDropdownChange(goal, e.target.value)}
                            >
                              {Object.entries(GOAL_CONFIG.getOptions(goal)).map(([key, value]) => (
                                <option key={key} value={value}>{value}</option>
                              ))}
                            </select>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="info-text">No goals selected yet.</p>
                  )}
                </div>
                
                <div className="section">
                  <h4>Available Goals</h4>
                  <p>Select goals to add:</p>
                  
                  <div className="goal-options">
                    <div className="goal-grid">
                      {Object.entries(availableGoalsSelection).map(([goal, isSelected]) => (
                        <div 
                          key={goal} 
                          className={`goal-card ${isSelected ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`goal-${goal}`}
                            checked={isSelected}
                            onChange={() => toggleAvailableGoal(goal)}
                          />
                          <label htmlFor={`goal-${goal}`}>{goal}</label>
                        </div>
                      ))}
                    </div>
                    
                    <button 
                      className="add-selected-button"
                      onClick={addSelectedGoals}
                      disabled={!Object.values(availableGoalsSelection).some(val => val)}
                    >
                      Add Selected Goals
                    </button>
                  </div>
                  
                  <div className="navigation-buttons">
                    <button 
                      className="submit-button" 
                      onClick={() => setStep(3)}
                      disabled={selectedGoals.length === 0}
                    >
                      Next
                    </button>
                    <button className="submit-button" onClick={() => setStep(1)}>
                      Back
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <p className="error">Customer data not available. Please select another customer.</p>
            )}
          </div>
        )}
        
        {/* Step 3: Goal Planning */}
        {step === 3 && (
          <div className="goal-planning-flow">
            {renderGoalPlanning()}
          </div>
        )}
        
        {/* Step 4: Insurance Details */}
        {step === 4 && 
          <InsuranceDetails 
            formData={formData}
            onFieldChange={handleInsuranceChange}
            onBack={() => setStep(3)}
            onContinue={() => setStep(5)}
          />
        }
        
        {/* Step 5: Review and Submit */}
        {step === 5 && <ReviewAndSubmit formData={formData} />}

        
        {/* Success Modal */}
        <Modal show={showModal} onClose={() => {
          setShowModal(false);
          resetForm();
        }}>
          <h2>🎉 Form Submitted Successfully!</h2>
          <p>Thank you for your response.</p>
        </Modal>
      </div>
    </div>
  );
}

export default FinancialPlanning;